import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Icon from '@ant-design/icons';
import { Position } from '@kemu-io/kemu-core/types';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { DragLayerMonitor, useDrag } from 'react-dnd';
import { usePreviewStateFull } from 'react-dnd-preview/dist/cjs/usePreview';
import { usePreview } from 'react-dnd-preview';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { ReactComponent as SnippetIcon } from './snippet-icon.svg';
import styles from './KemuSnippet.module.css';
import { parseKemuSnippet } from './helper';
import { DroppableTypes } from '@common/constants';
import { LogicMapperContext } from '@common/context/LogicMapperContext/LogicMapperContext';
import { pasteFromClipboardAction } from '@src/features/LogicMapper/reducers/clipboardReducers';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import useTranslation from '@hooks/useTranslation';
import useGetCurrentCanvasZoom from '@hooks/useGetCurrentCanvasZoom';
import { fixRelativeDocsUrl, getDocsBasePath } from '@common/utils';

type Props = {
  snippetCode: string;
  docsUrl: string;
  onIsDragging?: (isDragging: boolean) => void;
}

type KemuSnippetDragItem = {
  type: string; // Ensure type is part of the item if needed elsewhere
  code: string;
  rect: DOMRect | null;
  // Add other data you need to pass
}

const PreviewSize = {
  width: 80,
  height: 80,
};

const calculateClickOffset = (monitor: DragLayerMonitor) => {
  const initialClickPosition = monitor.getInitialClientOffset();
  const sourceOffset = monitor.getInitialSourceClientOffset();

  return {
    x: (initialClickPosition?.x || 0) - (sourceOffset?.x || 0),
    y: (initialClickPosition?.y || 0) - (sourceOffset?.y || 0),
  };
};

const DragPreview = (props: {
  onCalculatedDiff?: (diff: { x: number, y: number }) => void;
}) => {
  const { onCalculatedDiff } = props;
  const { logicMapperContainerEl } = useContext(LogicMapperContext);
  const { display, style, itemType, item, monitor } = usePreview() as usePreviewStateFull<KemuSnippetDragItem>;
  const diffRef = useRef<{ x: number, y: number } | null>(null);
  const abortRendering = itemType !== DroppableTypes.KemuSnippet
    || !display
    || !logicMapperContainerEl;


  useEffect(() => {
    if (!abortRendering && !diffRef.current) {
      const clickDiff = calculateClickOffset(monitor);
      diffRef.current = clickDiff;
      // IMPORTANT: We report the initial click offset so that the drop location
      // can be adjusted based on where in the image the drag started
      onCalculatedDiff?.(diffRef.current);
    }
  }, [monitor, onCalculatedDiff, abortRendering]);

  if (abortRendering || !diffRef.current) {
    return null;
  }


  // NOTE: we must use a portal because the ServiceHelpPanel uses transforms that would break the 'fixed' position of this element
  return createPortal(
    <div
      className={`${styles.PreviewWrapper}`}
      style={{
        ...style,
        width: PreviewSize.width,
        height: PreviewSize.height,
        left: (diffRef.current?.x || 0) - PreviewSize.width / 2,
        top: (diffRef.current?.y || 0) - PreviewSize.height / 2,
      }}
    >
      <div className={styles.DragPreviewContent}>
        <Icon component={SnippetIcon} />
      </div>
    </div>,
    logicMapperContainerEl
  );
};

const KemuSnippet = (props: Props) => {
  const dragRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const t = useTranslation();
  const dragSourceDiff = useRef<{ x: number, y: number } | null>(null);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const getZoomLevel = useGetCurrentCanvasZoom();
  const { onIsDragging } = props;

  const handleCalculatedDiff = useCallback((diff: { x: number, y: number }) => {
    dragSourceDiff.current = diff;
  }, []);

  const parsedCode = useMemo(() => {
    const parsed = parseKemuSnippet(props.snippetCode);
    return parsed;
  }, [props.snippetCode]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [{ isDragging }, drag, preview] = useDrag<any, Position, any>(() => ({
    type: DroppableTypes.KemuSnippet,
    item: () => {
      const node = dragRef.current;
      // Capture initial state when drag starts
      const rect = node ? node.getBoundingClientRect() : null;

      return {
        rect,
      };
    },
    end: (item, monitor) => {
      const dropLocation = monitor.getDropResult();
      const zoom = getZoomLevel();
      if (dropLocation && currentRecipe.poolId) {
        const clickDiff = dragSourceDiff.current;
        const diffX = clickDiff?.x || 0;
        const diffY = clickDiff?.y || 0;

        // Adjust the drop location based on where in the image the click happened
        // and the current zoom level
        const adjustedDropLocation = {
          x: (dropLocation.x + (diffX / zoom) - (PreviewSize.width / 2) / zoom) * zoom,
          y: (dropLocation.y + (diffY / zoom) - (PreviewSize.height / 2) / zoom) * zoom,
        };

        dispatch(pasteFromClipboardAction({
          recipeId: currentRecipe.poolId,
          thingId: DEFAULT_THING_ID,
          mapData: parsedCode.code,
          mouseLocation: adjustedDropLocation,
        }));
      }
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [currentRecipe, getZoomLevel]);


  useEffect(() => {
    const emptyImage = getEmptyImage();
    preview(emptyImage);
    onIsDragging?.(isDragging);
  }, [preview, isDragging, onIsDragging]);

  // Connect the dragRef to the drag function
  drag(dragRef);

  const legend = parsedCode.label ? `${parsedCode.label}` : t('KemuSnippet.DefaultLegend');
  // NOTE: this url doesn't get fixed by the MarkdownViewer because
  // this is a custom markdown component.
  const previewUrl = parsedCode.preview
    ? fixRelativeDocsUrl(parsedCode.preview, getDocsBasePath(parsedCode.preview, props.docsUrl))
    : '';
  const isVideo = parsedCode.preview?.endsWith('.mp4');


  return (
    <>
      <div className={styles.SnippetWrapper}>
        {parsedCode.preview ? (
          <div className={styles.ImgPreviewContainer} ref={dragRef}>
            <div className={styles.ImageLegend}>
              <Icon component={SnippetIcon} />
              <span>{legend}</span>
            </div>
            <div className={styles.ImgPreview}>
              {isVideo ? (
                <video
                  src={previewUrl}
                  autoPlay
                  loop
                  muted
                  controls={parsedCode.controls !== false}
                />
              ) : (
                <img
                  src={previewUrl}
                  alt={parsedCode.alt || 'Snippet preview'}
                  width={parsedCode.width}
                  height={parsedCode.height}
                />
              )}
              <div className={styles.ImageSnippetIcon}>
                <Icon component={SnippetIcon} />
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.Legend} ref={dragRef}>
            <Icon component={SnippetIcon} />
            <span>{legend}</span>
          </div>
        )}
      </div>

      {isDragging && (
        <DragPreview onCalculatedDiff={handleCalculatedDiff} />
      )}
    </>
  );
};

export default KemuSnippet;
